import React, { useEffect, useState } from 'react'
import { FiDownload, FiLoader } from 'react-icons/fi';
import { RxCross2 } from 'react-icons/rx';
import * as XLSX from 'xlsx';

//Excel preview modal
const ExcelPreviewModal = ({ isOpen, onClose, file }) => {
    const [sheetData, setSheetData] = useState([]);
    const [sheetNames, setSheetNames] = useState([]);
    const [activeSheet, setActiveSheet] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [workbook, setWorkbook] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (isOpen && file) {
            loadExcelFile();
        }
    }, [isOpen, file]);

    const loadExcelFile = async () => {
        setLoading(true);
        setError(null);

        try {
            // READ DIRECTLY FROM FILE OBJECT (BLOB)
            const arrayBuffer = await file.arrayBuffer();

            const wb = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });

            setWorkbook(wb);
            setSheetNames(wb.SheetNames);
            loadSheet(wb, 0);

        } catch (error) {
            console.error('Excel parse error:', error);
            setError('Failed to parse Excel file');
        } finally {
            setLoading(false);
        }
    };
    // Helper to convert Excel ARGB/RGB to CSS hex
    const getCssHex = (colorObj) => {
        if (!colorObj || !colorObj.rgb) return null;
        let rgb = colorObj.rgb;
        // Sometimes sheetjs returns rgb without alpha, sometimes with it
        if (rgb.length === 8) {
            const a = rgb.substring(0, 2);
            const r = rgb.substring(2, 4);
            const g = rgb.substring(4, 6);
            const b = rgb.substring(6, 8);
            // Ignore alpha if it's FF (fully opaque)
            return a.toUpperCase() === 'FF' ? `#${r}${g}${b}` : `#${r}${g}${b}${a}`;
        } else if (rgb.length === 6) {
            return `#${rgb}`;
        }
        return null;
    };

    const loadSheet = (wb, sheetIndex) => {
        const worksheet = wb.Sheets[wb.SheetNames[sheetIndex]];

        const data = [];
        if (worksheet['!ref']) {
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            for (let R = range.s.r; R <= range.e.r; ++R) {
                const row = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                    const cell = worksheet[cellAddress];
                    row.push(cell ? cell : null);
                }
                data.push(row);
            }
        }
        setSheetData(data);
        setActiveSheet(sheetIndex);
    };

    const handleSheetChange = (index) => {
        if (workbook) {
            // Check if there are unsaved changes on current sheet before switching?
            // For now, assume changes are kept in the workbook object or state
            // It's safer to sync current sheetData back to workbook before switching!
            if (hasChanges) {
                const ws = workbook.Sheets[sheetNames[activeSheet]];
                sheetData.forEach((row, R) => {
                    row.forEach((cellObj, C) => {
                        if (cellObj) {
                            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                            ws[cellAddress] = cellObj;
                        }
                    });
                });
            }
            loadSheet(workbook, index);
        }
    };

    const handleCellChange = (rowIndex, cellIndex, value) => {
        setHasChanges(true);
        setSheetData(prev => {
            const newData = [...prev];
            const newRow = [...newData[rowIndex]];
            const oldCell = newRow[cellIndex] || { t: 's' };
            newRow[cellIndex] = { ...oldCell, v: value, w: String(value) };
            newData[rowIndex] = newRow;
            return newData;
        });
    };

    const handleDownload = () => {
        if (hasChanges && workbook) {
            // sync active sheet before download
            const ws = workbook.Sheets[sheetNames[activeSheet]];
            sheetData.forEach((row, R) => {
                row.forEach((cellObj, C) => {
                    if (cellObj) {
                        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                        ws[cellAddress] = cellObj;
                    }
                });
            });

            const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: "application/octet-stream" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name || 'edited_excel.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            return;
        }

        const url = window.URL.createObjectURL(file);

        const a = document.createElement('a');
        a.href = url;
        a.download = file.name || 'excel.xlsx';

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="bg-zinc-900 rounded-xl shadow-2xl w-[95vw] h-[90vh] max-w-7xl flex flex-col border border-zinc-700"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-zinc-100 truncate">
                            {file?.original_filename || file?.filename || 'Excel Preview'}
                        </h2>
                        <p className="text-xs text-zinc-400 mt-0.5">
                            {file?.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${isEditing ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'}`}
                        >
                            {isEditing ? 'View Mode' : 'Edit Sheet'}
                        </button>
                        <button
                            onClick={handleDownload}
                            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg transition-colors flex items-center gap-2 text-sm"
                        >
                            <FiDownload className="w-4 h-4" />
                            {hasChanges ? 'Save / Download' : 'Download'}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors text-zinc-400 hover:text-zinc-200"
                        >
                            <RxCross2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Sheet Tabs */}
                {!loading && !error && sheetNames.length > 0 && (
                    <div className="flex items-center gap-1 px-6 py-2 border-b border-zinc-800 overflow-x-auto no-scrollbar bg-zinc-900/50">
                        {sheetNames.map((name, index) => (
                            <button
                                key={index}
                                onClick={() => handleSheetChange(index)}
                                className={`
                  px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap
                  ${activeSheet === index
                                        ? 'bg-zinc-700 text-zinc-100'
                                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                                    }
                `}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-auto bg-zinc-900">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3">
                            <FiLoader className="w-8 h-8 text-zinc-400 animate-spin" />
                            <div className="text-zinc-400">Loading Excel file...</div>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
                            <div className="text-red-400 text-center">{error}</div>
                            <button
                                onClick={handleDownload}
                                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download File
                            </button>
                        </div>
                    ) : sheetData.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-zinc-400">No data in this sheet</div>
                        </div>
                    ) : (
                        <div className="p-4">
                            <div className="overflow-auto rounded-lg border border-zinc-700">
                                <table className="border-collapse min-w-full bg-zinc-800">
                                    <tbody>
                                        {sheetData.map((row, rowIndex) => (
                                            <tr
                                                key={rowIndex}
                                                className={`border-b border-zinc-700 last:border-b-0 ${rowIndex === 0 ? 'sticky top-0 z-10' : ''}`}
                                            >
                                                {row.map((cellObj, cellIndex) => {
                                                    const val = cellObj ? cellObj.v : '';
                                                    const formatVal = cellObj ? cellObj.w || val : '';

                                                    let bgColor = null;
                                                    let textColor = null;
                                                    let fontWeight = 'normal';
                                                    let fontStyle = 'normal';
                                                    let textDecoration = 'none';

                                                    if (cellObj && cellObj.s) {
                                                        if (cellObj.s.fill && cellObj.s.fill.fgColor) {
                                                            bgColor = getCssHex(cellObj.s.fill.fgColor);
                                                        }
                                                        if (cellObj.s.font) {
                                                            if (cellObj.s.font.color) textColor = getCssHex(cellObj.s.font.color);
                                                            if (cellObj.s.font.bold) fontWeight = 'bold';
                                                            if (cellObj.s.font.italic) fontStyle = 'italic';
                                                            if (cellObj.s.font.underline) textDecoration = 'underline';
                                                            if (cellObj.s.font.strike) textDecoration = textDecoration === 'underline' ? 'underline line-through' : 'line-through';
                                                        }
                                                    }

                                                    const isHeader = rowIndex === 0;

                                                    return (
                                                        <td
                                                            key={cellIndex}
                                                            style={{
                                                                backgroundColor: bgColor || undefined,
                                                                color: textColor || undefined,
                                                                fontWeight: fontWeight !== 'normal' ? fontWeight : undefined,
                                                                fontStyle: fontStyle !== 'normal' ? fontStyle : undefined,
                                                                textDecoration: textDecoration !== 'none' ? textDecoration : undefined
                                                            }}
                                                            className={`
                              px-4 py-2.5 text-sm border-r border-zinc-700 last:border-r-0 whitespace-nowrap
                              ${!bgColor && isHeader ? 'bg-zinc-700 text-zinc-100 font-semibold' : ''}
                              ${!bgColor && !isHeader ? 'text-zinc-300 bg-zinc-800 hover:bg-zinc-750' : ''}
                            `}
                                                        >
                                                            {isEditing && !isHeader ? (
                                                                <input
                                                                    type="text"
                                                                    value={formatVal}
                                                                    onChange={(e) => handleCellChange(rowIndex, cellIndex, e.target.value)}
                                                                    className="w-full bg-transparent outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                                                                    style={{ color: textColor || 'inherit' }}
                                                                />
                                                            ) : (
                                                                formatVal !== null && formatVal !== undefined && formatVal !== '' ? String(formatVal) : '—'
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExcelPreviewModal
